import { useState, useEffect, useMemo } from 'react';
import { ThemeProvider } from '@emotion/react';
import styled from '@emotion/styled';
import { Box, Typography, Avatar, Divider, List, ListItemButton, Collapse } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import FactoryOutlinedIcon from '@mui/icons-material/FactoryOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { useUser } from 'services/context/UserContext';
import { useAppContext } from 'services/context/AppContext';
import { getClientFirebaseFlows } from 'services/api/ClientHandler';
import { ROUTE_CONFIG, MENU_SECTIONS } from 'config/routes.config';
import { hasAnyRole, ALL_PAGE_ACCESS_ROLES, ROLES } from 'config/roles';
import themeV3 from 'themes/v3/AppTheme';

const APP_VERSION = 'V3.1.1';

/** Icones du menu principal, par id de route. */
const MENU_ICONS = {
  'store-manager': StorefrontOutlinedIcon,
  'my-region': LocationOnOutlinedIcon,
  'my-warehouse': WarehouseOutlinedIcon,
  decision: BarChartOutlinedIcon,
  operation: HubOutlinedIcon,
  compliance: VerifiedOutlinedIcon,
  historic: HistoryOutlinedIcon,
  'my-orders': LocalShippingOutlinedIcon,
  industrial: FactoryOutlinedIcon,
  'my-network': HubOutlinedIcon,
};

/**
 * Panneau Parametres (second volet) — liste à plat.
 * Chaque entrée est sa propre catégorie (pas d'en-tête de section qui
 * répèterait le nom de l'entrée). Certaines pages n'existent pas encore :
 * la route guard renverra vers access-denied/404 tant qu'elles ne sont pas creees.
 */
// Métadonnées d'affichage (label + icône) des sous-vues de "Par lieu", indexées par
// le segment enfant déclaré dans routes.config.js (view-by-location.children).
// Le chemin, lui, est dérivé automatiquement de la config : changer le path du parent
// (ou d'un enfant) se répercute ici sans édition manuelle.
const VIEW_BY_LOCATION_META = {
  upstream: { label: 'Vue Amont', icon: WarehouseOutlinedIcon },
  downstream: { label: 'Vue Aval', icon: LocationOnOutlinedIcon },
};

const viewByLocationRoute = ROUTE_CONFIG.find((r) => r.id === 'view-by-location');
const VIEW_BY_LOCATION_BASE = viewByLocationRoute ? `/${viewByLocationRoute.path}` : '';
const VIEW_BY_LOCATION_ITEMS = (viewByLocationRoute?.children || [])
  .filter((child) => VIEW_BY_LOCATION_META[child.path])
  .map((child) => ({
    label: VIEW_BY_LOCATION_META[child.path].label,
    path: `${VIEW_BY_LOCATION_BASE}/${child.path}`,
    icon: VIEW_BY_LOCATION_META[child.path].icon,
  }));

/**
 * Construit la liste des items du panneau Paramètres depuis ROUTE_CONFIG.
 * Une route apparaît si menu.show === true, menu.section === 'settings' et menu.icon défini.
 * Le profil est toujours placé en premier.
 * Ajouter une page settings = ajouter une entrée dans ROUTE_CONFIG avec menu.icon. Rien à changer ici.
 */
/**
 * Retrouve l'entree ROUTE_CONFIG (ou son enfant) dont le path correspond au pathname courant.
 * Sert a savoir si la page affichee appartient au meme tabGroup qu'un item de menu qui, lui,
 * pointe vers l'onglet par defaut de ce groupe (ex: "Actions" et "Connexion" partagent le
 * tabGroup 'action' mais ont des routes/paths distincts).
 */
function findRouteConfigByPath(pathname) {
  const stripped = pathname.replace(/^\//, '');
  for (const route of ROUTE_CONFIG) {
    if (route.path === stripped) return route;
    for (const child of (route.children || [])) {
      if (`${route.path}/${child.path}` === stripped) return child;
    }
  }
  return undefined;
}

function buildSettingsItems(userRoles) {
  const items = [];

  const toItem = (route, path) => ({
    label: route.menu.label,
    path,
    icon: route.menu.icon || SettingsOutlinedIcon,
    beta: route.menu.beta,
    new: route.menu.new,
    tabGroup: route.page?.tabGroup,
  });

  // Profile en premier (déclaré après les routes principales dans ROUTE_CONFIG)
  const profileRoute = ROUTE_CONFIG.find((r) => r.id === 'profile');
  if (profileRoute?.menu?.show && profileRoute.menu.section === 'settings' && hasAnyRole(userRoles, profileRoute.roles)) {
    items.push(toItem(profileRoute, `/${profileRoute.path}`));
  }

  for (const route of ROUTE_CONFIG) {
    if (route.id === 'profile') continue;
    if (route.menu?.show && route.menu?.section === 'settings' && hasAnyRole(userRoles, route.roles)) {
      items.push(toItem(route, `/${route.path}`));

      for (const child of (route.children || [])) {
        if (child.menu?.show && child.menu?.section === 'settings') {
          const childRoles = child.roles || route.roles;
          if (hasAnyRole(userRoles, childRoles)) {
            items.push(toItem(child, `/${route.path}/${child.path}`));
          }
        }
      }
    }
  }

  return items;
}

const Sidebar = styled(Box)(({ theme }) => ({
  display: 'flex',
  height: '100vh',
  position: 'fixed',
  top: 0,
  left: 0,
  zIndex: 1200,
  backgroundColor: theme.palette.background.paper,
}));

const Column = styled(Box)(({ theme }) => ({
  width: 220,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRight: `1px solid ${theme.palette.border.main}`,
  backgroundColor: theme.palette.background.paper,
}));

const UserBlock = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(2),
  cursor: 'pointer',
  '&:hover': { backgroundColor: theme.palette.border.light },
}));

const NavItem = styled(ListItemButton, { shouldForwardProp: (p) => p !== 'active' })(({ theme, active }) => ({
  margin: theme.spacing(0.25, 1),
  padding: theme.spacing(1, 1.5),
  borderRadius: 8,
  gap: theme.spacing(1.5),
  color: active ? theme.palette.text.primary : theme.palette.text.secondary,
  backgroundColor: active ? theme.palette.border.light : 'transparent',
  fontWeight: active ? 600 : 400,
  fontSize: 13,
  '& svg': { fontSize: 18 },
  '&:hover': { backgroundColor: theme.palette.border.light },
}));

const SubNavItem = styled(NavItem)(({ theme }) => ({
  paddingLeft: theme.spacing(5.5),
}));

/** Ligne verticale continue à gauche des sous-items, pour rattacher visuellement le groupe. */
const SubNavList = styled(Box)(({ theme }) => ({
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: theme.spacing(3.5),
    top: 4,
    bottom: 4,
    width: 2,
    backgroundColor: theme.palette.border.main,
  },
}));

const NewBadge = styled(Box)(({ theme }) => ({
  fontSize: 9,
  lineHeight: 1,
  fontWeight: 600,
  fontFamily: 'Inter, sans-serif',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  borderRadius: 6,
  padding: '3px 4px',
}));

const BetaBadge = styled(NewBadge)(({ theme }) => ({
  backgroundColor: theme.palette.warning.main,
  color: theme.palette.warning.contrastText,
  marginLeft: 'auto',
}));

const PanelTitle = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.5,
  textTransform: 'uppercase',
  color: theme.palette.text.secondary,
  padding: theme.spacing(2, 2, 1, 2),
}));

const SettingsItem = styled(NavItem, { shouldForwardProp: (p) => p !== 'active' })(({ theme, active }) => ({
  color: active ? theme.palette.primary.main : theme.palette.text.secondary,
  backgroundColor: active ? theme.palette.primary.surface : 'transparent',
  '&:hover': { backgroundColor: active ? theme.palette.primary.surface : theme.palette.border.light },
}));

/** Mise en avant : couleur primaire au lieu du gris neutre des autres NavItem. */
const ContactItem = styled(NavItem)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 600,
  '&:hover': { backgroundColor: theme.palette.primary.surface },
}));

const Footer = styled(Box)(({ theme }) => ({
  marginTop: 'auto',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.border.light}`,
}));

const LogoSquare = styled(Avatar)(({ theme }) => ({
  width: 32,
  height: 32,
  borderRadius: 8,
  backgroundColor: theme.palette.primary.main,
  fontSize: 16,
  fontWeight: 700,
}));

/** Items visibles d'une section donnee, filtres par roles utilisateur. */
const menuItemsForSection = (userRoles, sections) =>
  ROUTE_CONFIG.filter(
    (r) => r.menu?.show && sections.includes(r.menu.section) && hasAnyRole(userRoles, r.roles),
  );

/** Initiales pour l'avatar (ex: "Guillaume Dournel" -> "GD"). */
const initials = (name = '') =>
  name.split(' ').map((part) => part[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

const COLUMN_WIDTH = 220;

export default function Menu({ onWidthChange }) {
  const { user } = useUser();
  const { openTutorial } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [profileViewOpen, setProfileViewOpen] = useState(false);

  // Informe le layout parent de la largeur occupee (volet Parametres compris).
  useEffect(() => {
    onWidthChange?.(settingsOpen ? COLUMN_WIDTH * 2 : COLUMN_WIDTH);
  }, [settingsOpen, onWidthChange]);

  const userRoles = useMemo(() => user?.roles || [], [user?.roles]);

  // Flux du client -- sert a masquer "Mes commandes" (my-orders) pour les clients sans flux
  // menager (page dediee au suivi des commandes B2C, non pertinente en pro/eic uniquement).
  const [flows, setFlows] = useState([]);
  useEffect(() => {
    if (!user?.id || !user?.token) return;
    (async () => {
      const res = await getClientFirebaseFlows(user.id, user.token);
      if (res?.status === 200) setFlows(res.data ?? []);
    })();
  }, [user?.id, user?.token]);
  const hasMenagerFlow = flows.some((f) => f.type?.toLowerCase().includes('menager'));

  const mainItems = menuItemsForSection(userRoles, ['main']).filter(
    (route) => route.id !== 'my-orders' || hasMenagerFlow,
  );
  const partnerItems = menuItemsForSection(userRoles, ['partner']);
  const industrialRoute = partnerItems.find((route) => route.id === 'industrial');
  const otherPartnerItems = partnerItems.filter((route) => route !== industrialRoute);
  const adminItems = menuItemsForSection(userRoles, ['admin']);
  const AdminIcon = MENU_SECTIONS.admin.icon;
  // Bloc "Mon réseau" — header cliquable vers /my-network (toujours déplié), puis Historique / Opérationnel
  // et (admin) Par lieu / Par flux ; les autres rôles voient leurs pages accessibles à plat.
  const networkItems = menuItemsForSection(userRoles, ['network']);
  const inNetworkGroup = hasAnyRole(userRoles, ALL_PAGE_ACCESS_ROLES);
  const myNetworkRoute = networkItems.find((route) => route.id === 'my-network');
  const myNetworkPath = myNetworkRoute ? `/${myNetworkRoute.path}` : '/my-network';
  const viewByFlowRoute = networkItems.find((route) => route.id === 'view-by-flow');
  const NetworkIcon = MENU_SECTIONS.network.icon;
  // Bloc "Vue par profil" (Mon magasin / Mon entrepôt / Ma région) — déroulant pour
  // l'admin uniquement ; un manager voit uniquement SA page, à plat en tête de menu.
  const profileViewItems = menuItemsForSection(userRoles, ['profile-view']);
  const inProfileViewGroup = hasAnyRole(userRoles, [ROLES.ADMIN.id]);
  const ProfileViewIcon = MENU_SECTIONS['profile-view'].icon;
  const IndustrialIcon = MENU_ICONS.industrial;
  const settingsItems = useMemo(() => buildSettingsItems(userRoles), [userRoles]);
  const hasSettings = settingsItems.length > 0;

  const isActive = (path) => location.pathname === path;
  // Le tabGroup courant permet de garder un item de menu selectionne quand on est sur un
  // onglet de sa page (ex: "Actions") qui n'a pas exactement le path du menu (ex: "Connexion").
  const currentTabGroup = findRouteConfigByPath(location.pathname)?.page?.tabGroup;
  const isSettingsItemActive = (item) => isActive(item.path) || (!!item.tabGroup && item.tabGroup === currentTabGroup);
  const settingsActive = settingsOpen || settingsItems.some(isSettingsItemActive);
  const locationActive = !!VIEW_BY_LOCATION_BASE && location.pathname.startsWith(VIEW_BY_LOCATION_BASE);

  return (
    <ThemeProvider theme={themeV3}>
      <Sidebar>
        <Column>
          <UserBlock onClick={() => navigate('/settings/profile')}>
            <Avatar sx={{ width: 34, height: 34, bgcolor: 'primary.surface', color: 'primary.main', fontSize: 13, fontWeight: 700 }}>
              {initials(user?.name)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 14 }} noWrap>{user?.name}</Typography>
              {user?.email && user.email !== user.name && (
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }} noWrap>{user.email}</Typography>
              )}
            </Box>
          </UserBlock>
          <Divider />

          <List sx={{ pt: 1, overflowY: 'auto' }}>
            {/* Managers (store/warehouse/group) : leur page à plat, en tête de menu */}
            {!inProfileViewGroup && profileViewItems.map((route) => {
              const path = `/${route.path}`;
              const Icon = MENU_ICONS[route.id];
              return (
                <NavItem key={route.id} active={isActive(path)} onClick={() => { navigate(path); setSettingsOpen(false); }}>
                  {Icon && <Icon />}
                  {route.menu.label}
                  {route.menu.new && <NewBadge>new</NewBadge>}
                  {route.menu.beta && <BetaBadge>BETA</BetaBadge>}
                </NavItem>
              );
            })}

            {/* Bloc "Mon réseau" (admin/account/project) — sinon items accessibles à plat (ex: Historique manager) */}
            {networkItems.length > 0 && (inNetworkGroup ? (
              <>
                <NavItem active={isActive(myNetworkPath)} onClick={() => { navigate(myNetworkPath); setSettingsOpen(false); }}>
                  <NetworkIcon />
                  {MENU_SECTIONS.network.label}
                </NavItem>
                <SubNavList>
                  {networkItems.filter((route) => route.id !== 'my-network' && route.id !== 'view-by-flow').map((route) => {
                    const path = `/${route.path}`;
                    const isLocation = route.id === 'view-by-location';
                    return (
                      <Box key={route.id}>
                        <SubNavItem
                          active={isLocation ? locationActive : isActive(path)}
                          onClick={() => { navigate(path); setSettingsOpen(false); }}
                        >
                          {route.menu.label}
                          {route.menu.new && <NewBadge>new</NewBadge>}
                          {route.menu.beta && <BetaBadge>BETA</BetaBadge>}
                        </SubNavItem>
                        {/* Par flux : juste après Par lieu, avant Vue Amont / Vue aval */}
                        {isLocation && viewByFlowRoute && (
                          <SubNavItem
                            active={isActive(`/${viewByFlowRoute.path}`)}
                            onClick={() => { navigate(`/${viewByFlowRoute.path}`); setSettingsOpen(false); }}
                          >
                            {viewByFlowRoute.menu.label}
                          </SubNavItem>
                        )}
                        {/* Vue Amont / Vue aval : items à plat juste après Par lieu (enfants upstream/downstream, sans icône) */}
                        {isLocation && VIEW_BY_LOCATION_ITEMS.map((item) => (
                          <SubNavItem
                            key={item.path}
                            active={isActive(item.path)}
                            onClick={() => { navigate(item.path); setSettingsOpen(false); }}
                          >
                            {item.label}
                          </SubNavItem>
                        ))}
                      </Box>
                    );
                  })}
                </SubNavList>
              </>
            ) : (
              networkItems.map((route) => {
                const path = `/${route.path}`;
                const Icon = MENU_ICONS[route.id];
                return (
                  <NavItem key={route.id} active={isActive(path)} onClick={() => { navigate(path); setSettingsOpen(false); }}>
                    {Icon && <Icon />}
                    {route.menu.label}
                  </NavItem>
                );
              })
            ))}

            {/* Industriel (partner/admin) — juste après le groupe Mon réseau, cf. spec TASK-542 */}
            {industrialRoute && (
              <NavItem
                active={isActive(`/${industrialRoute.path}`)}
                onClick={() => { navigate(`/${industrialRoute.path}`); setSettingsOpen(false); }}
              >
                {IndustrialIcon && <IndustrialIcon />}
                {industrialRoute.menu.label}
              </NavItem>
            )}

            {otherPartnerItems.map((route) => {
              const path = `/${route.path}`;
              const Icon = MENU_ICONS[route.id];
              return (
                <NavItem key={route.id} active={isActive(path)} onClick={() => { navigate(path); setSettingsOpen(false); }}>
                  {Icon && <Icon />}
                  {route.menu.label}
                </NavItem>
              );
            })}

            {mainItems.map((route) => {
              const path = `/${route.path}`;
              const Icon = MENU_ICONS[route.id];
              return (
                <NavItem key={route.id} active={isActive(path)} onClick={() => { navigate(path); setSettingsOpen(false); }}>
                  {Icon && <Icon />}
                  {route.menu.label}
                </NavItem>
              );
            })}

            {/* Bloc "Vue par profil" (admin uniquement) : Mon magasin / Mon entrepôt / Ma région */}
            {inProfileViewGroup && profileViewItems.length > 0 && (
              <>
                <NavItem active={false} onClick={() => { setProfileViewOpen((open) => !open); setSettingsOpen(false); }}>
                  <ProfileViewIcon />
                  {MENU_SECTIONS['profile-view'].label} {profileViewOpen ? '▾' : '▸'}
                </NavItem>
                <Collapse in={profileViewOpen}>
                  <SubNavList>
                    {profileViewItems.map((route) => {
                      const path = `/${route.path}`;
                      return (
                        <SubNavItem key={route.id} active={isActive(path)} onClick={() => { navigate(path); setSettingsOpen(false); }}>
                          {route.menu.label}
                          {route.menu.new && <NewBadge>new</NewBadge>}
                          {route.menu.beta && <BetaBadge>BETA</BetaBadge>}
                        </SubNavItem>
                      );
                    })}
                  </SubNavList>
                </Collapse>
              </>
            )}

            {adminItems.length > 0 && (
              <>
                <NavItem active={false} onClick={() => { setAdminOpen((open) => !open); setSettingsOpen(false); }}>
                  <AdminIcon />
                  {MENU_SECTIONS.admin.label} {adminOpen ? '▾' : '▸'}
                </NavItem>
                <Collapse in={adminOpen}>
                  <SubNavList>
                    {adminItems.map((route) => {
                      const path = `/${route.path}`;
                      return (
                        <SubNavItem key={route.id} active={isActive(path)} onClick={() => { navigate(path); setSettingsOpen(false); }}>
                          {route.menu.label}
                          {route.menu.new && <NewBadge>new</NewBadge>}
                          {route.menu.beta && <BetaBadge>BETA</BetaBadge>}
                        </SubNavItem>
                      );
                    })}
                  </SubNavList>
                </Collapse>
              </>
            )}

            {hasSettings && (
              <NavItem active={settingsActive} onClick={() => setSettingsOpen((open) => !open)}>
                <SettingsOutlinedIcon />
                Paramètres
              </NavItem>
            )}

            <NavItem active={false} onClick={openTutorial}>
              <HelpOutlineOutlinedIcon />
              Tutoriel
            </NavItem>
          </List>

          <Box sx={{ marginTop: 'auto' }}>
            <ContactItem
              component="a"
              href="mailto:tech@mobiuspack.com?subject=%5BDashboard%20MOBIUSpack%5D%20Nouveau%20message"
              target="_blank"
              rel="noopener noreferrer"
            >
              <MailOutlineIcon />
              Nous contacter
            </ContactItem>

            <Footer>
              <LogoSquare variant="rounded">M</LogoSquare>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 13 }}>MOBIUSpack</Typography>
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{APP_VERSION}</Typography>
              </Box>
            </Footer>
          </Box>
        </Column>

        {settingsOpen && (
          <Column>
            <PanelTitle>Paramètres</PanelTitle>
            <List sx={{ pt: 0, overflowY: 'auto' }}>
              {settingsItems.map((item) => {
                const Icon = item.icon;
                return (
                  <SettingsItem key={item.path} active={isSettingsItemActive(item)} onClick={() => navigate(item.path)}>
                    <Icon />
                    {item.label}
                    {item.new && <NewBadge>new</NewBadge>}
                    {item.beta && <BetaBadge>BETA</BetaBadge>}
                  </SettingsItem>
                );
              })}
            </List>
          </Column>
        )}
      </Sidebar>
    </ThemeProvider>
  );
}
